import React, { useLayoutEffect, useEffect, useRef, useState } from "react";
import Sidebar from "../../components/Sidebar";
import PanelCard from "../../components/Card/PanelCard";
import {
  Button,
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import useSWR from "swr";
import MinuteInput from "../../components/Inputs/Time";
import { loadRequest, loadRequestOrigin } from "../../store/ducks/panel/actions";
import { ApplicationState } from "../../store";
import FacebookCircularProgress from "../../components/Loading/index"
import useScroll from "../../utils/useScroll";
import { checkViewPermission } from "../../utils/permissions";
import NoPermission from "../../components/Erros/NoPermission";
import { IOriginList } from "../../store/ducks/panel/types";

export default function Panel() {
  const dispatch = useDispatch();

  const [scrollAuto, setScrollAuto] = useState<boolean>(true);
  const [origin, setOrigin] = useState<string>("1 - TODAS");
  const [originSelected, setOriginSelected] = useState<IOriginList>({
    id: 1,
    origin_name: "TODAS",
  });

  const [intervalSeconds, setIntervalSeconds] = useState(60);
  const intervalTime = intervalSeconds * 1000;

  const cardContainer = useRef(null);
  const limit = 3000;

  const panelState = useSelector((state: ApplicationState) => state.panel);


  const [initialLoad, setInitialLoad] = useState(true);

  const isLoading = panelState.loadingData || panelState.loadingOrigin;

  const showLoader = initialLoad && isLoading;

  const rightsOfLayoutState = useSelector(
    (state: ApplicationState) => state.layout.data.rights
  );

  //scrol quando time mudar
  useLayoutEffect(() => {
    if (!scrollAuto) return;

    const container = cardContainer.current;
    if (!container) return;

    container.scrollTop = 0;

  }, [panelState.data, scrollAuto]);


  useEffect(() => {
    if (initialLoad && !isLoading && panelState.data.length > 0 && panelState.origin.length > 0) {
      setInitialLoad(false);
    }
  }, [isLoading, panelState.data, panelState.origin, initialLoad]);

  useEffect(() => {
    dispatch(loadRequestOrigin());
  }, []);

  useSWR(
    originSelected ? ["polling", intervalTime, originSelected.id] : null,
    () => {

      dispatch(
        loadRequest({
          page: 1,
          limit: limit,
          origin_id: originSelected.id,
          origin_name: originSelected.origin_name,
        })
      );
      return true;
    },
    {
      refreshInterval: intervalTime,
      keepPreviousData: true,
      dedupingInterval: 0,
    }
  );

  const getTimeByLimit = (limit: number) => {
    const minLimit = 1;
    const maxLimit = 100;
    const minTime = 200;
    const maxTime = 1000;

    const normalizedTime = Math.max(minLimit, Math.min(maxLimit, limit));

    const time =
      minTime +
      ((maxTime - minTime) * (normalizedTime - minLimit)) /
      (maxLimit - minLimit);

    return time;
  };

  useScroll(cardContainer, getTimeByLimit(limit), scrollAuto);

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const valueToFind = event.target?.value.replace(/.*?\s*-\s*/, "");
    const foundOriginObject = Array.isArray(panelState.origin)
      ? panelState.origin.find((origin) => origin.origin_name === valueToFind)
      : undefined;

    setOriginSelected(foundOriginObject || { id: 1, origin_name: "TODAS" });
    setOrigin(event.target?.value as string);
    setInitialLoad(true)
  };

  return (
    <Sidebar >
      {checkViewPermission("panel", JSON.stringify(rightsOfLayoutState)) ? (
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >

          <Box display="flex" justifyContent="space-between" paddingX={2}
            mb={{ xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5, xl: 4, }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              color="primary"
              fontSize={[undefined, undefined, undefined, undefined, "4rem"]}
            >
              Painel Assistencial
            </Typography>


            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 3 }}>
              <MinuteInput
                value={intervalSeconds}
                onChange={(newValue) => setIntervalSeconds(newValue)}
              />

              <FormControl
                sx={{
                  width: {
                    xs: 150, sm: 150, md: 150, lg: 150, xl: 350,
                  },
                }}
              >
                <InputLabel
                  id="origin-label"
                  sx={{
                    fontSize: {
                      xs: "0.7rem", sm: "0.8rem", md: "0.9rem", lg: "1rem", xl: "2rem",
                    },
                  }}
                >
                  Origem
                </InputLabel>

                <Select
                  labelId="origin-label"
                  label="Origem"
                  value={origin || ""}
                  onChange={handleChangeSelect}
                  size="small"
                  sx={{
                    width: "100%",
                    fontSize: {
                      xs: "0.7rem", sm: "0.8rem", md: "0.9rem", lg: "1rem", xl: "2rem",
                    },
                    height: {
                      xs: 35, sm: 35, md: 40, lg: 40, xl: 80,
                    },
                  }}
                >
                  {Array.isArray(panelState.origin) &&
                    panelState.origin.map((origin, i) => (
                      <MenuItem
                        key={i}
                        sx={{
                          fontSize: {
                            xs: "0.7rem", sm: "0.8rem", md: "0.9rem", lg: "1rem", xl: "2.5rem",
                          },
                        }}
                        value={`${origin.id} - ${origin.origin_name}`}
                      >
                        {origin.id} - {origin.origin_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {showLoader ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <FacebookCircularProgress />
            </Box>
          ) : (
            <Box
              ref={cardContainer}
              sx={{
                p: 2,
                pb: 20,
                flex: 1,
                overflowY: "scroll",
              }}
            >
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(3, 1fr)",
                  xl: "repeat(4, 1fr)",
                }}
                gap={[2, 2, 2, 2, 5]}
              >
                {!Array.isArray(panelState.data) || panelState.data.length === 0 ?
                  (<Typography
                    color={"secondary.main"}
                    fontSize={{
                      xs: "1rem",
                      sm: "1rem",
                      lg: "1.5rem",
                      md: "1.5rem",
                      xl: "3.5rem"
                    }}>
                    Nenhum paciente encontrado
                  </Typography>)
                  :
                  panelState.data.map((item) => (
                    <PanelCard key={item.attendance_id} data={item} />
                  ))
                }
              </Box>
            </Box>
          )}

          <Button
            sx={{
              position: "absolute",
              right: 40,
              bottom: 22,
              fontSize: {
                xs: 10, xl: 30,
              }
            }}
            variant="contained"
            onClick={() => setScrollAuto(!scrollAuto)}
          >
            Scroll automático:
            {scrollAuto ? (
              <Typography
                fontSize={{ xs: 10, xl: 30, }}
                fontWeight={600}
                marginLeft={0.5}
                color={"theme.palette.success.main"}
              >
                Ativado
              </Typography>
            ) : (
              <Typography
                fontSize={{ xs: 10, xl: 30, }}
                fontWeight={600}
                marginLeft={0.5}
                color={"theme.palette.error.main"}
              >
                Desativado
              </Typography>
            )}
          </Button>
        </Container>
      ) : (
        <NoPermission />
      )
      }
    </Sidebar >
  );
}